import type { DeepReadonly } from 'vue';

import type { RecipeIngredient } from '@apps/main/modules/recipe/ingredients';
import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';
import { getGuestRating } from '@apps/main/new-desktop/modules/customersRating';
import { getImageUrl } from '@apps/main/modules/api/hotels';

import type {
  CarrierName,
  ChronologicalItinerary,
  DestinationStaticInfo,
  Dictionary,
  FerryTicketCategory,
  HotelAlternativeOptionWithBasicRooms,
  IngredientId,
  ItineraryWithPrice,
  OverviewDestinationStaticInfo,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';
import { assertValue } from '@libs/utils';
import type { RouteWithDetails } from '@libs/maps';
import {
  isOwnTransportIngredientResponseWithStaticInfo,
  isStayIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';

import type {
  AccommodationUpsellCardData,
  ItineraryAccommodationUpsellSegment,
  ItineraryAccommodationUpsellLoaderSegment,
  ItinerarySegment,
} from '../../models';
import { ItinerarySegmentType } from '../../models';

import { getBookableTransportSegment } from './tripOverviewBookableTransportSegmentLogic';
import {
  getAccommodationDate,
  getTopHotelAmenities,
} from './tripOverviewHotelRelatedSegmentsLogic';
import { getOwnTransportRelatedSegment } from './tripOverviewOwnTransportRelatedSegmentsLogic';

interface GetFlightOnlyItineraryParams {
  chronologicalItinerary: ChronologicalItinerary[];
  getIngredient: (id: string) => RecipeIngredient;
  isSuggestedTransport: (ingredientId: IngredientId) => boolean;
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined;
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined;
  selectedDate: Date;
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  currency: string;
  upsellItineraryDates: DeepReadonly<DateWithItinerary[]>;
  upsellDestinationStaticInfo: DeepReadonly<
    Dictionary<OverviewDestinationStaticInfo>
  >;
  formatMoney: (amount: number, currency?: string) => string;
  calculatedRoute: RouteWithDetails;
}

export function getFlightOnlyItinerary(
  params: GetFlightOnlyItineraryParams
): ItinerarySegment[] {
  // NOTE: Chronological itinerary is empty when there is no selectedItineraryWithStaticInfo
  if (!params.chronologicalItinerary?.length) {
    return [];
  }

  const itinerary = params.chronologicalItinerary.reduce(
    (
      segments: ItinerarySegment[],
      itineraryByPeriod: ChronologicalItinerary
    ) => {
      itineraryByPeriod.ingredients.forEach((ingredient, index) => {
        const bookableTransportSegment = getBookableTransportSegment(
          ingredient,
          params.getIngredient,
          params.isSuggestedTransport,
          params.ferryTicketCategoryDictionary,
          params.userSelectedFerryTicketCategory,
          params.getFlightAllCarriers,
          params.getFerryAllCarriers,
          params.currency,
          params.selectedDate
        );

        if (bookableTransportSegment) {
          segments.push(bookableTransportSegment);
        } else if (ingredient.ingredientType === IngredientType.OwnStay) {
          const accommodationUpsellSegment = getAccommodationUpsellSegment({
            upsellItineraryDates: params.upsellItineraryDates,
            upsellDestinationStaticInfo: params.upsellDestinationStaticInfo,
            selectedDate: params.selectedDate,
            ingredientId: ingredient.ingredientId,
            formatMoney: params.formatMoney,
          });
          segments.push(accommodationUpsellSegment);
        } else {
          if (!isOwnTransportIngredientResponseWithStaticInfo(ingredient)) {
            return;
          }

          const hotelRelatedIngredient =
            itineraryByPeriod.ingredients[index - 1];

          if (
            !hotelRelatedIngredient ||
            !isStayIngredientResponseWithStaticInfo(hotelRelatedIngredient)
          ) {
            return;
          }

          // TODO: Half of parameters here are hardcoded and make no sense.
          // Not sure how this should behave when user changes ingredient types in the recipe.
          // Keep it here until Marucs gives more details
          const ownTransportSegment = getOwnTransportRelatedSegment(
            ingredient,
            hotelRelatedIngredient,
            index,
            params.getIngredient,
            params.calculatedRoute,
            true,
            false,
            false,
            true
          );

          if (ownTransportSegment) {
            segments.push(ownTransportSegment);
          }
        }
      });

      return segments;
    },
    []
  );

  const firstFlight = itinerary[0];
  const ownStaySegment = itinerary[1];
  const lastFlight = itinerary[2];

  if (
    firstFlight &&
    ownStaySegment &&
    lastFlight?.segmentType === ItinerarySegmentType.Flight
  ) {
    return [firstFlight, lastFlight, ownStaySegment];
  }

  return itinerary;
}

export function getAccommodationUpsellSegment(
  params: AccommodationUpsellSegmentParams
):
  | ItineraryAccommodationUpsellSegment
  | ItineraryAccommodationUpsellLoaderSegment {
  const loaderSegment: ItineraryAccommodationUpsellLoaderSegment = {
    segmentType: ItinerarySegmentType.AccommodationUpsellLoader,
    context: {
      ingredientId: params.ingredientId,
    },
  };
  const upsellItineraryDate = params.upsellItineraryDates.find(
    (upsellItineraryDate) => {
      const itineraryDate = new SemboDate(upsellItineraryDate.date);

      return itineraryDate.isSameDay(params.selectedDate);
    }
  );

  if (upsellItineraryDate && upsellItineraryDate.itinerary) {
    const numberOfNights =
      upsellItineraryDate.itinerary.hotelOnlyItinerary[0]?.numberOfNights;

    assertValue(
      numberOfNights,
      'flightOnlyItinerary.ts > getAccommodationUpsellSegment > numberOfNights'
    );

    const segment: ItineraryAccommodationUpsellSegment = {
      segmentType: ItinerarySegmentType.AccommodationUpsell,
      productCardData: getAccomodationUpsellProductCardData(
        params.upsellDestinationStaticInfo,
        upsellItineraryDate.itinerary,
        params.formatMoney
      ),
      context: {
        ingredientId: params.ingredientId,
        numberOfNights,
      },
    };

    return segment.productCardData.length > 0 ? segment : loaderSegment;
  }

  return loaderSegment;
}

interface AccommodationUpsellSegmentParams {
  upsellItineraryDates: DeepReadonly<DateWithItinerary[]>;
  upsellDestinationStaticInfo: DeepReadonly<
    Dictionary<OverviewDestinationStaticInfo>
  >;
  selectedDate: Date;
  ingredientId: IngredientId;
  formatMoney: (amount: number, currency?: string) => string;
}

function getAccomodationUpsellProductCardData(
  upsellDestinationStaticInfo: DeepReadonly<
    Dictionary<OverviewDestinationStaticInfo>
  >,
  itineraryWithPrice: DeepReadonly<ItineraryWithPrice>,
  formatMoney: (amount: number, currency?: string) => string
): AccommodationUpsellCardData[] {
  const ingredient = itineraryWithPrice.hotelOnlyItinerary[0];

  assertValue(
    ingredient,
    'flightOnlyItinerary.ts > getAccommodationUpsellProductCardData > ingredient'
  );

  const suggestions = ingredient.suggestions;

  assertValue(
    suggestions,
    'flightOnlyItinerary.ts > getAccommodationUpsellProductCardData > suggestions'
  );

  return suggestions.reduce(
    (
      acc: AccommodationUpsellCardData[],
      suggestion: DeepReadonly<HotelAlternativeOptionWithBasicRooms>
    ): AccommodationUpsellCardData[] => {
      const hotelStaticData =
        upsellDestinationStaticInfo[suggestion.code + ingredient.ingredientId];

      const topAmenities = 4;
      if (hotelStaticData) {
        const smallImageInPx = 600;
        const data: AccommodationUpsellCardData = {
          amenities:
            getTopHotelAmenities(hotelStaticData.amenities, topAmenities)[0] ??
            [],
          boardBasis: undefined,
          date: getAccommodationDate(
            ingredient.date,
            ingredient.numberOfNights
          ),
          guestRating: getGuestRating(hotelStaticData.guestRatings),
          hotelDestination: getAccommodationSubtitleText(hotelStaticData),
          hotelName: hotelStaticData.name,
          imgUrls: hotelStaticData.images.map((image) =>
            getImageUrl(image, undefined, smallImageInPx, smallImageInPx)
          ),
          price: formatMoney(suggestion.prices.totalPrice.amount),
          starRating: hotelStaticData.semboSunRating,
          hotelCode: hotelStaticData.hotelCode,
        };

        acc.push(data);
      }

      return acc;
    },
    []
  );
}

// TODO: Duplicated
export function getAccommodationSubtitleText(
  product:
    | DeepReadonly<DestinationStaticInfo>
    | DeepReadonly<OverviewDestinationStaticInfo>
): string {
  if (product.mainPolygon) {
    return product.mainPolygon.fullName;
  }

  if (product?.address?.city) {
    return product.address.country
      ? `${product.address.city}, ${product.address.country}`
      : product.address.city;
  }

  return '';
}
